// components/PipelineCalculator.js
// Pipeline pressure drop (Weymouth / Panhandle), CSA Z662 design checks, hydrate formation
import React, { useState, useMemo } from 'react';
import { COMPONENTS } from '../fluid-properties';

// ═══════════════════════════════════════════════════════════════════════════
// Helper math
// ═══════════════════════════════════════════════════════════════════════════

function dAKZ(Pr, Tr) {
  // Dranchuk-Abou-Kassem Z-factor correlation
  const rhoR0 = 0.27 * Pr / Tr;
  let rhoR = rhoR0;
  for (let i = 0; i < 50; i++) {
    const A1=0.3265, A2=-1.0700, A3=-0.5339, A4=0.01569, A5=-0.05165;
    const A6=0.5475, A7=-0.7361, A8=0.1844, A9=0.1056, A10=0.6134, A11=0.7210;
    const f = -rhoR + (A1+A2/Tr+A3/Tr**3+A4/Tr**4+A5/Tr**5)*rhoR
      + (A6+A7/Tr+A8/Tr**2)*rhoR**2
      - A9*(A7/Tr+A8/Tr**2)*rhoR**5
      + A10*(1+A11*rhoR**2)*(rhoR**2/Tr**3)*Math.exp(-A11*rhoR**2)
      + 0.27*Pr/Tr;
    const df = -1 + (A1+A2/Tr+A3/Tr**3+A4/Tr**4+A5/Tr**5)
      + 2*(A6+A7/Tr+A8/Tr**2)*rhoR
      - 5*A9*(A7/Tr+A8/Tr**2)*rhoR**4
      + A10*(2*rhoR/Tr**3)*(1+A11*rhoR**2)*Math.exp(-A11*rhoR**2)
      + A10*(rhoR**2/Tr**3)*2*A11*rhoR*Math.exp(-A11*rhoR**2)
      - A10*(rhoR**2/Tr**3)*A11*rhoR**2*2*A11*rhoR*Math.exp(-A11*rhoR**2);
    const delta = f / df;
    rhoR -= delta;
    if (Math.abs(delta) < 1e-7) break;
  }
  return 0.27 * Pr / (rhoR * Tr);
}

function mixturePseudo(comp) {
  // Lee-Kesler mixing rules for Tpc / Ppc
  let Tpc = 0, Ppc = 0, MW = 0;
  const comps = Object.values(COMPONENTS);
  for (const [name, xi_pct] of Object.entries(comp)) {
    const c = comps.find(x => x.name === name);
    if (!c || xi_pct <= 0) continue;
    const xi = xi_pct / 100;
    Tpc += xi * c.Tc;
    Ppc += xi * c.Pc;
    MW  += xi * c.MW;
  }
  return { Tpc, Ppc, MW: MW || 18 };
}

function weymouthDP(Q_e3m3d, D_mm, L_km, P1_kPa, T_C, Sg, Z) {
  // Weymouth equation (GPSA SI version)
  // Q [m3/d] = 3.7435e-3 * E * D^2.667 * [(P1^2 - P2^2) / (Sg * T * L * Z)]^0.5
  // Rearranged for P2:
  // P2 = sqrt( P1^2 - (Q / (3.7435e-3 * D^2.667))^2 * (Sg * T * L * Z) )
  const T_K = T_C + 273.15;
  const Q_m3d = Q_e3m3d * 1000;
  const E = 1.0; // Efficiency factor
  const C = 3.7435e-3;
  
  const term1 = Q_m3d / (C * E * Math.pow(D_mm, 2.667));
  const P2_sq = Math.pow(P1_kPa, 2) - (Math.pow(term1, 2) * Sg * T_K * L_km * Z);
  const P2_kPa = Math.sqrt(Math.max(0, P2_sq));
  
  return { P2_kPa, dP_kPa: P1_kPa - P2_kPa };
}

// Hydrate formation temperature – simplified Katz chart + acid gas correction
function hydrateTemp_C(P_kPa, Sg, co2_frac, h2s_frac) {
  const P_bar = P_kPa / 100;
  // Base Katz correlation: T_h ≈ (a*log10(P) + b) × Sg^0.5
  const a = 8.0, b = 3.0;
  let T = (a * Math.log10(P_bar) + b) * Math.sqrt(Sg);
  // Acid gas adjustments
  T += co2_frac * 10 * Math.log10(P_bar);
  T += h2s_frac * 15 * Math.log10(P_bar);
  if (P_bar > 100) T *= 0.97;
  return T;
}

// Hammerschmidt inhibitor concentration required for a given temp depression
function hammerschmidt(deltaT_C, inhibitor) {
  // ΔT = KH × W / (M × (100 − W))   →   W = 100ΔTM / (KH + ΔTM)
  const KH = inhibitor === 'methanol' ? 1297 : 2222; // MEG
  const M  = inhibitor === 'methanol' ? 32.04 : 62.07;
  const W  = (100 * deltaT_C * M) / (KH + deltaT_C * M); // wt%
  return Math.max(0, W);
}

// ═══════════════════════════════════════════════════════════════════════════
// CSA Z662 Design Checks
// ═══════════════════════════════════════════════════════════════════════════

const LOCATION_CLASSES = {
  'A': { F: 0.80, description: 'Isolated — remote, uninhabited' },
  'B': { F: 0.72, description: 'Sparsely populated — farms, ranches' },
  'C': { F: 0.56, description: 'Populated — subdivisions, shopping centres' },
  'D': { F: 0.44, description: 'Highly populated — dense commercial / residential' },
};

function csaHoopStress(P_kPa, OD_mm, t_mm) {
  // S_h = P × D / (2t)   [kPa × mm / mm = kPa]  → convert to MPa
  return (P_kPa * OD_mm) / (2 * t_mm) / 1000; // MPa
}

function csaMaxAllowablePressure(SMYS_MPa, F, t_mm, OD_mm) {
  // Barlow: P = 2 × S × F × t / OD   [MPa] → kPa
  return (2 * SMYS_MPa * F * t_mm / OD_mm) * 1000; // kPa
}

const PIPE_GRADES = {
  'X42': 290, 'X52': 359, 'X56': 386, 'X60': 414,
  'X65': 448, 'X70': 483, 'X80': 551,
};

// ═══════════════════════════════════════════════════════════════════════════
// Sub-component: Pressure Drop
// ═══════════════════════════════════════════════════════════════════════════

function PressureDropCalc({ composition }) {
  const [Q,    setQ]    = useState(500);    // e³m³/d
  const [D,    setD]    = useState(323.9);  // mm OD (12" pipe)
  const [wt,   setWt]   = useState(9.5);    // mm wall
  const [L,    setL]    = useState(50);     // km
  const [P1,   setP1]   = useState(8000);   // kPa
  const [T,    setT]    = useState(10);     // °C

  const { Tpc, Ppc, MW } = useMemo(() => mixturePseudo(composition), [composition]);
  const Sg   = MW / 28.96;
  const T_K  = T + 273.15;
  const Tr   = T_K / Tpc;
  const Pr   = (P1 / 100) / Ppc; // P1 bar / Ppc bar
  const Z    = dAKZ(Pr, Tr);
  const D_id = D - 2 * wt; // inside diameter mm

  const { P2_kPa, dP_kPa } = weymouthDP(Q, D_id, L, P1, T, Sg, Z);
  // Velocity v = Q_actual / Area
  // Q_actual = Q_std * (P_std / P_actual) * (T_actual / T_std) * Z
  const Q_std_m3s = (Q * 1000 / 86400);
  const v_ms = Q_std_m3s * (101.325 / P1) * (T_K / 288.15) * Z / (Math.PI * (D_id/2000)**2);

  return (
    <div className="tool-panel">
      <h3>Pipeline Pressure Drop — Weymouth Equation</h3>
      <p style={{color:'#8b9ab3', fontSize:13, marginBottom:16}}>
        Suitable for high-pressure gas pipelines (turbulent fully rough flow).
        Panhandle A/B give similar results for smaller diameters.
      </p>

      <div className="two-col-grid">
        <div className="input-group">
          <label>Flow Rate (e³m³/d)</label>
          <input type="number" value={Q} onChange={e=>setQ(+e.target.value)} />
        </div>
        <div className="input-group">
          <label>Pipe OD (mm)</label>
          <input type="number" value={D} onChange={e=>setD(+e.target.value)} />
        </div>
        <div className="input-group">
          <label>Wall Thickness (mm)</label>
          <input type="number" step="0.1" value={wt} onChange={e=>setWt(+e.target.value)} />
        </div>
        <div className="input-group">
          <label>Pipeline Length (km)</label>
          <input type="number" value={L} onChange={e=>setL(+e.target.value)} />
        </div>
        <div className="input-group">
          <label>Inlet Pressure (kPaa)</label>
          <input type="number" value={P1} onChange={e=>setP1(+e.target.value)} />
        </div>
        <div className="input-group">
          <label>Average Temperature (°C)</label>
          <input type="number" value={T} onChange={e=>setT(+e.target.value)} />
        </div>
      </div>

      <div className="result-cards">
        <div className="result-card highlight">
          <div className="result-label">Outlet Pressure</div>
          <div className="result-value">{P2_kPa.toFixed(0)} kPaa</div>
          <div className="result-note">{(P2_kPa/100).toFixed(2)} bar(a)</div>
        </div>
        <div className="result-card highlight">
          <div className="result-label">Pressure Drop</div>
          <div className="result-value">{dP_kPa.toFixed(0)} kPa</div>
          <div className="result-note">{(dP_kPa/L).toFixed(1)} kPa/km</div>
        </div>
        <div className="result-card">
          <div className="result-label">Z-factor</div>
          <div className="result-value">{Z.toFixed(4)}</div>
          <div className="result-note">Tr={Tr.toFixed(2)}, Pr={Pr.toFixed(2)}</div>
        </div>
        <div className="result-card">
          <div className="result-label">Gas SG / MW</div>
          <div className="result-value">{Sg.toFixed(3)}</div>
          <div className="result-note">MW = {MW.toFixed(2)} g/mol</div>
        </div>
        <div className="result-card">
          <div className="result-label">Approx Gas Velocity</div>
          <div className="result-value">{v_ms.toFixed(1)} m/s</div>
          <div className="result-note">ID = {D_id.toFixed(1)} mm</div>
        </div>
        <div className="result-card">
          <div className="result-label">Pseudo-criticals</div>
          <div className="result-value">{Tpc.toFixed(1)} K</div>
          <div className="result-note">Ppc = {Ppc.toFixed(2)} bar</div>
        </div>
      </div>

      {v_ms > 15 && (
        <div className="info-box warn">
          ⚠️ Gas velocity {v_ms.toFixed(1)} m/s exceeds typical design limit of 15 m/s — consider erosion / noise.
        </div>
      )}
      {P2_kPa < 2000 && (
        <div className="info-box warn">
          ⚠️ Outlet pressure below 2 000 kPa — check delivery spec and compressor suction requirements.
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Sub-component: CSA Z662 Design Calculator
// ═══════════════════════════════════════════════════════════════════════════

function CsaZ662Calc() {
  const [MAOP,     setMAOP]     = useState(8000);   // kPa
  const [OD,       setOD]       = useState(323.9);  // mm
  const [wt,       setWt]       = useState(9.5);    // mm
  const [grade,    setGrade]    = useState('X52');
  const [locClass, setLocClass] = useState('B');
  const [T_derating, setTDerating] = useState(false); // > 120°C
  const [joinFac, setJoinFac]  = useState(1.0);     // joint factor

  const SMYS = PIPE_GRADES[grade];
  const { F }  = LOCATION_CLASSES[locClass];
  const T_fac  = T_derating ? 0.867 : 1.0;
  const J      = joinFac;

  // Hoop stress at MAOP
  const S_h    = csaHoopStress(MAOP, OD, wt);
  // Allowable hoop stress per CSA Z662 Cl 4.3.1
  const S_allow = SMYS * F * T_fac * J;
  // MOP check
  const MOP_calc = csaMaxAllowablePressure(SMYS, F * T_fac * J, wt, OD);

  const utilisation = S_h / S_allow * 100;
  const pass = utilisation <= 100;

  // MWT (minimum wall thickness) to contain MAOP
  const t_min = (MAOP / 1000 * OD) / (2 * SMYS * F * T_fac * J); // mm

  return (
    <div className="tool-panel">
      <h3>CSA Z662 Pipeline Design Calculator</h3>
      <p style={{color:'#8b9ab3', fontSize:13, marginBottom:16}}>
        Hoop stress check and MOP per CSA Z662-23 Clause 4.3 (Barlow's formula).
        Applicable to oil &amp; gas pipelines in Alberta / Canada.
      </p>

      <div className="two-col-grid">
        <div className="input-group">
          <label>MAOP / Design Pressure (kPa)</label>
          <input type="number" value={MAOP} onChange={e=>setMAOP(+e.target.value)} />
        </div>
        <div className="input-group">
          <label>Pipe OD (mm)</label>
          <input type="number" value={OD} onChange={e=>setOD(+e.target.value)} />
        </div>
        <div className="input-group">
          <label>Wall Thickness (mm)</label>
          <input type="number" step="0.1" value={wt} onChange={e=>setWt(+e.target.value)} />
        </div>
        <div className="input-group">
          <label>Pipe Grade (CSA/API)</label>
          <select value={grade} onChange={e=>setGrade(e.target.value)}>
            {Object.entries(PIPE_GRADES).map(([g,s])=>(
              <option key={g} value={g}>{g} — SMYS {s} MPa</option>
            ))}
          </select>
        </div>
        <div className="input-group">
          <label>Location Class</label>
          <select value={locClass} onChange={e=>setLocClass(e.target.value)}>
            {Object.entries(LOCATION_CLASSES).map(([cls, d])=>(
              <option key={cls} value={cls}>Class {cls} (F={d.F}) — {d.description}</option>
            ))}
          </select>
        </div>
        <div className="input-group">
          <label>Joint Factor (Cl 4.3.2)</label>
          <select value={joinFac} onChange={e=>setJoinFac(+e.target.value)}>
            <option value="1.0">1.0 — Seamless or SAW (full inspection)</option>
            <option value="0.80">0.80 — ERW</option>
            <option value="0.60">0.60 — Furnace butt-weld</option>
          </select>
        </div>
        <div className="input-group" style={{paddingTop:8}}>
          <label style={{display:'flex', alignItems:'center', gap:8, fontWeight:'normal'}}>
            <input type="checkbox" checked={T_derating} onChange={e=>setTDerating(e.target.checked)} />
            Temperature derating (T &gt; 120°C — multiply by 0.867 per Cl 4.3.1)
          </label>
        </div>
      </div>

      <div className="result-cards" style={{marginTop:16}}>
        <div className={`result-card ${pass?'highlight':'highlight-fail'}`}>
          <div className="result-label">Hoop Stress Utilisation</div>
          <div className="result-value" style={{color: pass?'#4ecb71':'#e74c3c'}}>
            {utilisation.toFixed(1)}%
          </div>
          <div className="result-note">{pass ? '✓ PASS' : '✗ FAIL — exceeds allowable'}</div>
        </div>
        <div className="result-card">
          <div className="result-label">Hoop Stress at MAOP</div>
          <div className="result-value">{S_h.toFixed(1)} MPa</div>
        </div>
        <div className="result-card">
          <div className="result-label">Allowable Stress (S×F×T×J)</div>
          <div className="result-value">{S_allow.toFixed(1)} MPa</div>
          <div className="result-note">SMYS {SMYS} × F {F} × T {T_fac} × J {J}</div>
        </div>
        <div className="result-card">
          <div className="result-label">Max Allowable OP (calc)</div>
          <div className="result-value">{MOP_calc.toFixed(0)} kPa</div>
          <div className="result-note">{(MOP_calc/1000).toFixed(2)} MPa</div>
        </div>
        <div className="result-card">
          <div className="result-label">Min Wall Thickness (for MAOP)</div>
          <div className="result-value">{t_min.toFixed(2)} mm</div>
          <div className="result-note">Current: {wt} mm ({wt>=t_min?'✓ OK':'✗ TOO THIN'})</div>
        </div>
        <div className="result-card">
          <div className="result-label">D/t Ratio</div>
          <div className="result-value">{(OD/wt).toFixed(1)}</div>
          <div className="result-note">{OD/wt > 100 ? '⚠ Very thin-walled' : 'OK'}</div>
        </div>
      </div>

      <div className="info-box" style={{marginTop:12}}>
        <strong>Key clauses applied:</strong>
        <ul style={{margin:'6px 0 0 16px', fontSize:13}}>
          <li>Cl 4.3.1 — Barlow hoop stress formula: S = PD/(2t)</li>
          <li>Cl 4.3.1 — Design factor F by location class: A=0.80, B=0.72, C=0.56, D=0.44</li>
          <li>Cl 4.3.1 — Temperature derating factor (0.867 for T &gt; 120°C)</li>
          <li>Cl 4.3.2 — Joint factor for weld type</li>
          <li>Cl 4.3 — MOP = 2 × SMYS × F × T × J × t / OD</li>
        </ul>
        <em style={{fontSize:12}}>This tool covers hoop stress only. Longitudinal, combined, and thermal stress checks per Cl 4.7–4.9 are not included.</em>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Sub-component: Hydrate Formation Calculator
// ═══════════════════════════════════════════════════════════════════════════

function HydrateCalc({ composition }) {
  const [P_kPa, setP] = useState(8000);  // kPa
  const [T_C,   setT] = useState(5);     // °C (pipeline temperature)
  const [Q,     setQ] = useState(500);   // e³m³/d flow rate

  const { MW } = useMemo(() => mixturePseudo(composition), [composition]);
  const Sg = MW / 28.96;

  const co2_frac = (composition[COMPONENTS.CO2.name] || 0) / 100;
  const h2s_frac = (composition[COMPONENTS.H2S.name] || 0) / 100;

  // Hydrate formation temperature at the given pressure
  const T_h = hydrateTemp_C(P_kPa, Sg, co2_frac, h2s_frac);
  const margin = T_C - T_h;  // positive = safe, negative = in hydrate zone

  // Build P-T hydrate curve (500 kPa to P+5000)
  const curvePoints = [];
  for (let p = 500; p <= P_kPa + 5000; p += 200) {
    curvePoints.push({ P: p, Th: hydrateTemp_C(p, Sg, co2_frac, h2s_frac) });
  }

  // Inhibitor requirements (always show, highlight when needed)
  const depression = Math.max(0, T_h - T_C + 2); // 2°C safety margin
  const methanolConc = hammerschmidt(depression, 'methanol');
  const megConc      = hammerschmidt(depression, 'meg');

  // Injection rate estimate: ~0.02 kg water per e³m³ (rough field gas default)
  const waterKgD = Q * 0.02;
  const methanolLd = depression > 0
    ? (waterKgD * methanolConc / (100 - methanolConc)) / 0.791
    : 0;
  const megLd = depression > 0
    ? (waterKgD * megConc / (100 - megConc)) / 1.11
    : 0;

  // SVG chart dimensions
  const W = 560, H = 200, PAD = 45;
  const allT = curvePoints.map(p=>p.Th).concat([-15, 40]);
  const allP = curvePoints.map(p=>p.P/100);
  const minT = Math.min(...allT), maxT = Math.max(...allT);
  const minP = 0, maxP = Math.max(...allP);
  const tx = T => PAD + (T - minT) / (maxT - minT) * (W - PAD - 20);
  const ty = P => H - 20 - (P - minP) / (maxP - minP) * (H - 40);

  const curvePath = curvePoints.map((p,i)=>`${i===0?'M':'L'}${tx(p.Th).toFixed(1)},${ty(p.P/100).toFixed(1)}`).join(' ');

  return (
    <div className="tool-panel">
      <h3>Hydrate Formation Calculator</h3>
      <p style={{color:'#8b9ab3', fontSize:13, marginBottom:16}}>
        Simplified Katz chart correlation + Hammerschmidt inhibitor sizing.
        For sour gas (H₂S/CO₂) the curve is shifted — ensure composition is set on the main feed page.
      </p>

      <div className="two-col-grid">
        <div className="input-group">
          <label>Pipeline Pressure (kPaa)</label>
          <input type="number" value={P_kPa} onChange={e=>setP(+e.target.value)} />
        </div>
        <div className="input-group">
          <label>Pipeline Temperature (°C)</label>
          <input type="number" value={T_C} onChange={e=>setT(+e.target.value)} />
        </div>
        <div className="input-group">
          <label>Flow Rate (e³m³/d)</label>
          <input type="number" value={Q} onChange={e=>setQ(+e.target.value)} />
        </div>
      </div>

      {/* Status banner */}
      <div className={`status-banner ${margin >= 2 ? 'safe' : margin >= 0 ? 'warn' : 'danger'}`}
           style={{marginTop:16}}>
        {margin >= 2 ? (
          <>✅ <strong>Safe</strong> — Pipeline conditions are {margin.toFixed(1)}°C above hydrate formation temperature ({T_h.toFixed(1)}°C at {(P_kPa/100).toFixed(1)} bar)</>
        ) : margin >= 0 ? (
          <>⚠️ <strong>Marginal</strong> — Only {margin.toFixed(1)}°C above hydrate temperature ({T_h.toFixed(1)}°C). Consider inhibitor injection.</>
        ) : (
          <>🚨 <strong>Hydrate Risk</strong> — Pipeline temperature ({T_C}°C) is {Math.abs(margin).toFixed(1)}°C BELOW hydrate formation temperature ({T_h.toFixed(1)}°C). Inhibition required.</>
        )}
      </div>

      {/* Hydrate curve SVG */}
      <div style={{marginTop:20}}>
        <h4>Hydrate Formation Envelope — P-T Diagram</h4>
        <svg viewBox={`0 0 ${W} ${H+30}`} style={{width:'100%', background:'#0d1117', borderRadius:8}}>
          {/* Axes */}
          <line x1={PAD} y1={H-20} x2={W-20} y2={H-20} stroke="#444" />
          <line x1={PAD} y1={H-20} x2={PAD} y2={5} stroke="#444" />
          <text x={W/2} y={H+25} fill="#666" textAnchor="middle" fontSize={11}>Temperature (°C)</text>
          <text x={10} y={H/2} fill="#666" textAnchor="middle" fontSize={11}
            transform={`rotate(-90,10,${H/2})`}>Pressure (bar)</text>

          {/* Gridlines */}
          {[0, 20, 40, 60, 80, 100].filter(p=>p<=maxP).map(p=>(
            <g key={p}>
              <line x1={PAD} y1={ty(p)} x2={W-20} y2={ty(p)} stroke="#1a2030" />
              <text x={PAD-4} y={ty(p)+4} fill="#666" textAnchor="end" fontSize={9}>{p}</text>
            </g>
          ))}
          {[-10, 0, 10, 20, 30].filter(t=>t>=minT && t<=maxT).map(t=>(
            <g key={t}>
              <line x1={tx(t)} y1={H-20} x2={tx(t)} y2={5} stroke="#1a2030" />
              <text x={tx(t)} y={H-6} fill="#666" textAnchor="middle" fontSize={9}>{t}</text>
            </g>
          ))}

          {/* Hydrate zone fill */}
          <path d={curvePath + ` L${tx(minT).toFixed(1)},${ty(maxP).toFixed(1)} L${tx(minT).toFixed(1)},${ty(0).toFixed(1)}`}
            fill="#e74c3c" fillOpacity="0.15" />

          {/* Hydrate curve */}
          <path d={curvePath} fill="none" stroke="#e74c3c" strokeWidth="2" />

          {/* Current operating point */}
          <circle cx={tx(T_C)} cy={ty(P_kPa/100)} r="6"
            fill={margin >= 2 ? '#4ecb71' : margin >= 0 ? '#f39c12' : '#e74c3c'}
            stroke="#fff" strokeWidth="2" />

          {/* Labels */}
          <text x={tx(minT)+8} y={ty(maxP*0.6)} fill="#e74c3c" fontSize={10}>Hydrate Zone →</text>
          <text x={tx(T_C)+8} y={ty(P_kPa/100)-8} fill="#fff" fontSize={10}>
            Operating ({T_C}°C, {(P_kPa/100).toFixed(0)} bar)
          </text>
        </svg>
      </div>

      {/* Inhibitor requirements */}
      {depression > 0 && (
        <div style={{marginTop:20}}>
          <h4>Inhibitor Requirements — Hammerschmidt Equation</h4>
          <p style={{color:'#8b9ab3', fontSize:13}}>
            Depression required: {depression.toFixed(1)}°C (includes 2°C safety margin)
          </p>
          <div className="result-cards">
            <div className="result-card">
              <div className="result-label">Methanol Concentration</div>
              <div className="result-value">{methanolConc.toFixed(1)} wt%</div>
              <div className="result-note">in free water phase</div>
            </div>
            <div className="result-card">
              <div className="result-label">Methanol Injection Rate</div>
              <div className="result-value">{methanolLd.toFixed(0)} L/d</div>
              <div className="result-note">based on ~0.02 kg H₂O/e³m³</div>
            </div>
            <div className="result-card">
              <div className="result-label">MEG Concentration</div>
              <div className="result-value">{megConc.toFixed(1)} wt%</div>
              <div className="result-note">in free water phase</div>
            </div>
            <div className="result-card">
              <div className="result-label">MEG Injection Rate</div>
              <div className="result-value">{megLd.toFixed(0)} L/d</div>
              <div className="result-note">based on ~0.02 kg H₂O/e³m³</div>
            </div>
          </div>
          <div className="info-box warn" style={{marginTop:12}}>
            ⚠️ Water content estimate (0.02 kg/e³m³) is a rough field-gas default.
            Use a Mcketta-Wehe chart or simulation for accurate water content — inhibitor rates scale directly.
          </div>
        </div>
      )}

      <div className="info-box" style={{marginTop:16}}>
        <strong>About this correlation:</strong> Katz chart method (1945), valid for sweet-gas specific
        gravities 0.55–0.75, pressures 0.1–100 bar. H₂S/CO₂ shift the curve upward (increases hydrate temp).
        For rich sour gas use a process simulator (HYSYS/ProMax) for definitive results.
        Inhibitor sizing via Hammerschmidt equation — accurate to ±10–15% for methanol &lt;25 wt%, MEG &lt;50 wt%.
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Top-level PipelineCalculator
// ═══════════════════════════════════════════════════════════════════════════

const PipelineCalculator = ({ feedComposition }) => {
  const [activeTab, setActiveTab] = useState('pressuredrop');

  // Build a composition object keyed by component name for child components
  const composition = feedComposition || {
    [COMPONENTS.METHANE.name]:        85.0,
    [COMPONENTS.ETHANE.name]:          8.0,
    [COMPONENTS.PROPANE.name]:         3.0,
    [COMPONENTS.BUTANE.name]:          1.0,
    [COMPONENTS.PENTANE.name]:         0.5,
    [COMPONENTS.HEXANE.name]:          0.2,
    [COMPONENTS.NITROGEN.name]:        1.0,
    [COMPONENTS.CO2.name]:             1.0,
    [COMPONENTS.H2S.name]:             0.1,
    [COMPONENTS.WATER.name]:           0.2,
  };

  return (
    <div>
      <div className="sub-tab-nav">
        <button className={`sub-tab-btn${activeTab==='pressuredrop'?' active':''}`}
          onClick={()=>setActiveTab('pressuredrop')}>Pressure Drop</button>
        <button className={`sub-tab-btn${activeTab==='csaz662'?' active':''}`}
          onClick={()=>setActiveTab('csaz662')}>CSA Z662 Design</button>
        <button className={`sub-tab-btn${activeTab==='hydrate'?' active':''}`}
          onClick={()=>setActiveTab('hydrate')}>Hydrate Formation</button>
      </div>

      {activeTab === 'pressuredrop' && <PressureDropCalc composition={composition} />}
      {activeTab === 'csaz662'     && <CsaZ662Calc />}
      {activeTab === 'hydrate'     && <HydrateCalc composition={composition} />}
    </div>
  );
};

export default PipelineCalculator;
